import React, { useState, useRef, useEffect, useCallback } from "react";
import Navbar from "@/components/Navbar";
import BuyCreditsModal from "@/components/UpgradeModal";
import CreditsBar from "@/components/CreditsBar";
import LowBalanceBanner from "@/components/LowBalanceBanner";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { LANGUAGES, SOURCE_LANGUAGES, TONES } from "@/data/languages";
import { motion } from "framer-motion";
import { UploadCloud, FileText, Download, Loader2, File } from "lucide-react";

export default function Documents() {
  const { user, refresh } = useAuth();
  const [source, setSource] = useState("auto");
  const [target, setTarget] = useState("English");
  const [tone, setTone] = useState("natural");
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [docs, setDocs] = useState([]);
  const [showBuy, setShowBuy] = useState(false);
  const [buyReason, setBuyReason] = useState("");
  const inputRef = useRef();

  const remaining = user?.remaining ?? 0;
  const unlimited = user?.unlimited;

  const loadDocs = useCallback(async () => {
    try {
      const { data } = await api.get("/history", { params: { type: "document" } });
      setDocs(data);
    } catch (err) {
      console.error("Errore nel caricamento dei documenti", err);
    }
  }, []);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const pick = (f) => {
    if (!f) return;
    if (!/\.(pdf|docx|txt|md)$/i.test(f.name)) return toast.error("Formato non supportato. Usa PDF, DOCX o TXT.");
    setFile(f);
  };

  const openBuy = (reason) => { setBuyReason(reason || ""); setShowBuy(true); };

  const submit = async () => {
    if (!file) return;
    if (!unlimited && remaining <= 0) return openBuy("Hai esaurito le battute. Abbonati o acquista un pacchetto per tradurre documenti.");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("target_lang", target);
      fd.append("source_lang", source);
      fd.append("tone", tone);
      await api.post("/documents/translate", fd, { headers: { "Content-Type": "multipart/form-data" } });
      refresh();
      toast.success("Documento tradotto con successo!");
      setFile(null);
      loadDocs();
    } catch (e) {
      if (e.response?.status === 402) openBuy(apiErr(e.response?.data?.detail));
      else toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.translated_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore durante il download");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-[1100px] mx-auto px-4 md:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 mb-8 items-end">
          <div>
            <h1 className="font-heading text-3xl font-light tracking-tight">Traduzione documenti</h1>
            <p className="text-sm text-muted-foreground mt-1">PDF, Word (.docx) o TXT — fino a 10MB. Consuma battute dal saldo.</p>
          </div>
          <CreditsBar user={user} />
        </div>

        <LowBalanceBanner user={user} onBuy={() => openBuy("")} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div
              data-testid="dropzone"
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files[0]); }}
              onClick={() => inputRef.current?.click()}
              className={`cursor-pointer rounded-2xl border-2 border-dashed p-12 text-center transition-colors duration-300 ${
                dragging ? "border-brand-blue bg-brand-blue/5" : "border-border hover:border-brand-blue/50"
              }`}
            >
              <input ref={inputRef} type="file" accept=".pdf,.docx,.txt,.md" className="hidden"
                data-testid="file-input" onChange={(e) => pick(e.target.files[0])} />
              {file ? (
                <div className="flex flex-col items-center gap-2">
                  <File className="h-10 w-10 text-brand-blue" />
                  <p className="font-medium">{file.name}</p>
                  <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(0)} KB — clicca per cambiare</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <UploadCloud className="h-10 w-10 text-muted-foreground" />
                  <p className="font-medium">Trascina un file qui o clicca per caricare</p>
                  <p className="text-xs text-muted-foreground">PDF · DOCX · TXT</p>
                </div>
              )}
            </div>

            {loading && (
              <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-brand-blue" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Traduzione in corso...</p>
                  <div className="h-1 mt-2 rounded-full bg-secondary overflow-hidden"><div className="h-full w-1/2 tracing-beam" /></div>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4 rounded-2xl border border-border bg-card p-6 h-fit">
            <div className="space-y-1.5">
              <span className="text-[10px] font-mono-label uppercase tracking-wider text-muted-foreground">Da</span>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger data-testid="doc-source-lang" className="rounded-full"><SelectValue /></SelectTrigger>
                <SelectContent>{SOURCE_LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code}>{l.flag} {l.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <span className="text-[10px] font-mono-label uppercase tracking-wider text-muted-foreground">A</span>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger data-testid="doc-target-lang" className="rounded-full"><SelectValue /></SelectTrigger>
                <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code}>{l.flag} {l.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <span className="text-[10px] font-mono-label uppercase tracking-wider text-muted-foreground">Tono</span>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger data-testid="doc-tone" className="rounded-full font-mono text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{TONES.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button data-testid="doc-translate-btn" onClick={submit} disabled={!file || loading}
              className="w-full rounded-full bg-brand-blue hover:bg-brand-blue/90 text-white h-11 gap-2">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Traduci documento"}
            </Button>
          </div>
        </div>

        {docs.length > 0 && (
          <div className="mt-12">
            <h2 className="font-heading text-xl font-medium mb-4">Documenti tradotti</h2>
            <div className="space-y-2">
              {docs.map((d) => (
                <motion.div key={d.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  data-testid={`doc-row-${d.id}`}
                  className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3 hover:border-brand-blue/40 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="h-5 w-5 text-brand-pink shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{d.original_filename}</p>
                      <p className="text-xs text-muted-foreground">→ {d.target_lang}{d.chars ? ` · ${d.chars.toLocaleString("it-IT")} battute` : ""}</p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" data-testid={`download-${d.id}`} onClick={() => download(d)} className="rounded-full gap-1.5">
                    <Download className="h-3.5 w-3.5" /> Scarica
                  </Button>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
      <BuyCreditsModal open={showBuy} onOpenChange={setShowBuy} reason={buyReason} />
    </div>
  );
}
